import React from 'react';
import { User, Facebook, Instagram, Check, Loader2 } from 'lucide-react';
import { cn } from "@/lib/utils/cn";

interface Account {
  id: string;
  name: string;
  instagramAllowed: boolean;
}

interface AccountSelectorProps {
  accounts: Account[];
  isLoading: boolean;
  selectedAccountId: string | null;
  onSelectAccount: (id: string) => void;
  postToInstagram: boolean;
  hasConnectedAccounts: boolean;
}

export const AccountSelector: React.FC<AccountSelectorProps> = ({
  accounts,
  isLoading,
  selectedAccountId,
  onSelectAccount,
  postToInstagram,
  hasConnectedAccounts
}) => {
  return (
    <div className="space-y-3">
      <label className="text-sm font-medium text-gray-700">Account</label>
      <div className="space-y-2">
        {isLoading ? (
           <div className="flex items-center justify-center py-4 bg-gray-50 rounded-xl">
              <Loader2 className="h-5 w-5 text-gray-400 animate-spin" />
           </div>
        ) : accounts.length === 0 ? (
           <div className="text-center py-4 bg-gray-50 rounded-xl border border-dashed border-gray-200">
              <p className="text-sm text-gray-500">Keine Accounts gefunden</p>
           </div>
        ) : (
          accounts.map(acc => {
            const isActive = selectedAccountId === acc.id;
            const isDisabled = (postToInstagram && !acc.instagramAllowed) || !hasConnectedAccounts;
             
             return (
              <button
                key={acc.id}
                type="button"
                disabled={isDisabled}
                onClick={() => onSelectAccount(acc.id)}
                className={cn(
                  "w-full flex items-center justify-between p-3 rounded-xl border transition-all",
                  isActive 
                    ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500" 
                    : "border-gray-200 hover:border-gray-300 hover:bg-gray-50",
                  isDisabled && "opacity-50 cursor-not-allowed hover:bg-transparent hover:border-gray-200"
                )}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className={cn(
                    "h-8 w-8 rounded-full flex-shrink-0 flex items-center justify-center",
                    isActive ? "bg-blue-200 text-blue-700" : "bg-gray-100 text-gray-500"
                  )}>
                    <User className="h-4 w-4" />
                  </div>
                  <div className="flex flex-col items-start min-w-0">
                    <span className={cn("text-sm font-medium truncate max-w-[180px]", isActive ? "text-blue-900" : "text-gray-900")}>
                      {acc.name}
                    </span>
                    <div className="flex items-center gap-1.5 mt-0.5">
                       <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                         <Facebook className="h-3 w-3" />
                         <span className="hidden xs:inline">Page</span>
                       </span>
                       {acc.instagramAllowed && (
                         <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-pink-100 text-pink-700">
                           <Instagram className="h-3 w-3" />
                           <span className="hidden xs:inline">Insta</span>
                         </span>
                       )}
                    </div>
                  </div>
                </div>
                {isActive && <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />}
              </button>
             );
          })
        )}
      </div>
    </div>
  );
};
